// src/admin/FormEditor/components/FormReportSection.jsx
import { Info } from "lucide-react";
import { memo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateFormMetadata,
  updateFormReportMetadata,
} from "../store/formEditorSlice";

const REPORT_PROFILE_URL =
  process.env.NODE_ENV === "development"
    ? "http://10.98.15.79:9395/regform/report_profile/list"
    : "/regform/report_profile/list";

// InputField component
const InputField = memo(({ label, field, value, onChange, placeholder }) => {
  return (
    <div className="flex items-center gap-2">
      <span className="text-gray-700 w-12">{label}</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder}
        className="flex-1 rounded-md border p-2
               shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      />
    </div>
  );
});

// Main component
const FormReportSection = memo(() => {
  const dispatch = useDispatch();
  const formReportMetadata = useSelector(
    (state) => state.formEditor.formReportMetadata
  );

  // State for profiles
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch profiles
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const response = await fetch(REPORT_PROFILE_URL);
        if (!response.ok) throw new Error("Failed to fetch profiles");
        const data = await response.json();
        setProfiles(data.report_profile || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfiles();
  }, []);

  // Handle input changes
  const handleInputChange = useCallback(
    (field, value) => {
      const updates = {
        [field]: value === "" ? null : value,
      };
      dispatch(updateFormReportMetadata({ updates }));
    },
    [dispatch]
  );

  // Handle profile selection
  const handleProfileSelect = useCallback(
    (profileName) => {
      if (!profileName) return;

      const selectedProfile = profiles.find(
        (p) => p.profile_name === profileName
      );
      if (selectedProfile) {
        const updates = {
          to: selectedProfile.to || null,
          cc: selectedProfile.cc || null,
          bcc: selectedProfile.bcc || null,
        };
        dispatch(updateFormReportMetadata({ updates }));

        // Update contact_email from profile
        dispatch(
          updateFormMetadata({
            updates: {
              contact_email:
                selectedProfile.contact_email || selectedProfile.to || null,
            },
          })
        );
      }
    },
    [profiles, dispatch]
  );

  const fields = [
    {
      label: "To",
      field: "to",
      placeholder: "recipient1@example.com;recipient2@example.com",
    },
    {
      label: "CC",
      field: "cc",
      placeholder: "cc1@example.com;cc2@example.com",
    },
    {
      label: "BCC",
      field: "bcc",
      placeholder: "bcc1@example.com;bcc2@example.com",
    },
  ];

  return (
    <div className="space-y-4 p-2">
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Form Report</h2>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Info size={16} className="flex-shrink-0" />
          <span>
            Place the report meta json file under &lt;regform
            root&gt;/form_report_meta
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Quick Profile
        </label>
        <select
          className="w-full rounded-md border p-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          onChange={(e) => handleProfileSelect(e.target.value)}
          disabled={isLoading}
        >
          <option value="">Select a profile...</option>
          {profiles.map((profile) => (
            <option key={profile.profile_name} value={profile.profile_name}>
              {profile.profile_name}
            </option>
          ))}
        </select>
        {error && (
          <p className="text-sm text-red-500">
            Error loading profiles: {error}
          </p>
        )}
      </div>

      <div className="space-y-3">
        {fields.map(({ label, field, placeholder }) => (
          <InputField
            key={field}
            label={label}
            field={field}
            value={formReportMetadata[field]}
            onChange={handleInputChange}
            placeholder={placeholder}
          />
        ))}
      </div>
    </div>
  );
});

export default FormReportSection;
